/**
 * BIEU THUC KHOANG TRANG: "1-3,7,9-".
 *
 * Nguoi dung nghi theo so trang bat dau tu 1. Toan bo phan con lai cua ung dung
 * nghi theo chi so bat dau tu 0. Cho quy doi giua hai he do la DAY, va chi o
 * day - moi cho khac deu lam viec tren chi so 0.
 *
 * Nguyen tac: khong bao gio "doan y tot" cho mot bieu thuc sai. "1-3,abc" phai
 * BAO LOI chu khong duoc am tham tra ve [0,1,2]. Nguoi dung go nham mot ky tu
 * roi bam Trich xuat va nhan ve mot tep thieu trang la kieu hong khong ai phat
 * hien duoc cho toi khi da gui tep di.
 */

export type RangeParse =
  /** `indices` la chi so bat dau tu 0, DA sap xep tang dan va DA loai trung. */
  | { ok: true; indices: number[] }
  | { ok: false; error: string };

/**
 * Phan tich bieu thuc khoang trang.
 *
 * Chap nhan:
 *   "3"        -> trang 3
 *   "1-3"      -> trang 1,2,3
 *   "9-"       -> tu trang 9 den het
 *   "-4"       -> tu dau den trang 4
 *   "1-3, 7,9-" -> gop lai, khoang cach thua duoc bo qua
 *   "-"        -> toan bo tai lieu
 *
 * Tu choi: chu cai, so 0, so am, khoang nguoc ("5-2"), trang vuot qua so trang
 * that, muc rong do dau phay thua ("1,,3").
 */
export function parsePageRange(expr: string, pageCount: number): RangeParse {
  if (!Number.isInteger(pageCount) || pageCount <= 0) {
    return { ok: false, error: 'Tài liệu chưa có trang nào.' };
  }

  const trimmed = expr.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: 'Chưa nhập trang nào. Ví dụ: 1-3, 7, 9-' };
  }

  const out = new Set<number>();

  for (const rawPart of trimmed.split(',')) {
    const part = rawPart.trim();
    if (part.length === 0) {
      return { ok: false, error: 'Có mục trống giữa hai dấu phẩy. Ví dụ đúng: 1-3, 7, 9-' };
    }

    // Chap nhan ca dau gach ngang dai (en dash) vi no la thu Word va trinh
    // duyet tu dong thay vao khi nguoi dung dan noi dung tu noi khac.
    const normalized = part.replace(/[‐-―]/g, '-');

    const dash = normalized.indexOf('-');
    if (dash === -1) {
      const n = parsePageNumber(normalized);
      if (n === null) return { ok: false, error: `“${part}” không phải số trang hợp lệ.` };
      if (n > pageCount) {
        return { ok: false, error: `Tài liệu chỉ có ${pageCount} trang, không có trang ${n}.` };
      }
      out.add(n - 1);
      continue;
    }

    // Chi cho phep MOT dau gach. "1-3-5" la loi go, khong phai y dinh nao ca.
    if (normalized.indexOf('-', dash + 1) !== -1) {
      return { ok: false, error: `“${part}” có nhiều hơn một dấu gạch nối.` };
    }

    const leftText = normalized.slice(0, dash).trim();
    const rightText = normalized.slice(dash + 1).trim();

    const left = leftText.length === 0 ? 1 : parsePageNumber(leftText);
    const right = rightText.length === 0 ? pageCount : parsePageNumber(rightText);

    if (left === null) return { ok: false, error: `“${leftText}” không phải số trang hợp lệ.` };
    if (right === null) return { ok: false, error: `“${rightText}” không phải số trang hợp lệ.` };

    if (left > pageCount || right > pageCount) {
      return {
        ok: false,
        error: `Tài liệu chỉ có ${pageCount} trang, khoảng “${part}” vượt quá.`,
      };
    }
    if (left > right) {
      return { ok: false, error: `Khoảng “${part}” ngược: trang đầu lớn hơn trang cuối.` };
    }

    for (let n = left; n <= right; n++) out.add(n - 1);
  }

  if (out.size === 0) {
    return { ok: false, error: 'Biểu thức không chọn được trang nào.' };
  }

  return { ok: true, indices: [...out].sort((a, b) => a - b) };
}

/** So trang nguoi dung nhap: nguyen duong, khong dau, khong phan thap phan. */
function parsePageNumber(text: string): number | null {
  if (!/^\d+$/.test(text)) return null;
  const n = Number(text);
  if (!Number.isSafeInteger(n) || n < 1) return null;
  return n;
}

/**
 * Duong nguoc lai: chi so 0 -> bieu thuc doc duoc, gop cac doan lien tiep.
 * Dung de hien "Đang chọn: 1-3, 7" cho nguoi dung xac nhan truoc khi thao tac.
 */
export function formatPageRange(indices: readonly number[]): string {
  const sorted = [...new Set(indices)].sort((a, b) => a - b);
  if (sorted.length === 0) return '';

  const parts: string[] = [];
  let start = sorted[0] as number;
  let previous = start;

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    if (current !== undefined && current === previous + 1) {
      previous = current;
      continue;
    }
    parts.push(start === previous ? `${start + 1}` : `${start + 1}-${previous + 1}`);
    if (current === undefined) break;
    start = current;
    previous = current;
  }

  return parts.join(', ');
}
